// client/src/components/pages/OperationOverview/routePerformanceFigures.ts (ST-00218 P3)
//
// The Route Performance tab's variance. GET /route-summary/route-performance now sends each
// route's own matched figures (the one figures service): `adjustedDispatchWeight` (dispatched
// + BF − CF, on "Truck + time" routes also less received off-route and no receipt data) and
// `variancePct` against it; null with a data status other than 'ok' is "n/a" (no, or only
// partial, receipt data). An older response without the fields keeps the weighed formula
// (received − dispatched) / dispatched, which is what the tab showed before.
// ST-00298: only over an adjusted dispatch above 0.5 t (utils/variance); a route that received
// against nothing left dispatched is n/a and stays out of the totals variance.
import type { RoutePerformanceRow } from '../../../types/clientPages/operationOverview';
import { hasVarianceBase, varianceOf } from '../../../utils/variance';
import { isVarianceNa, varianceNaReason } from './overviewKpis';

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** The dispatch the variance is measured against. */
export const rowAdjustedDispatch = (r: RoutePerformanceRow): number =>
  isNum(r.adjustedDispatchWeight) ? r.adjustedDispatchWeight : r.dispatchWeight || 0;

/** The route's variance in %, or null = n/a. */
export const rowVariance = (r: RoutePerformanceRow): number | null => {
  if (isVarianceNa(r)) return null;
  const d = rowAdjustedDispatch(r);
  if (hasVarianceBase(d) && isNum(r.variancePct)) return r.variancePct;
  return varianceOf(r.receiptWeight || 0, d);
};

/** Why the route's variance is n/a, in words (the tooltip). */
export const rowVarianceNaReason = (r: RoutePerformanceRow): string =>
  varianceNaReason(r.dataStatus, r.receiptWeight || 0, rowAdjustedDispatch(r));

export interface RoutePerformanceTotals {
  /** Weighed, as the columns. */
  dW: number;
  rW: number;
  dC: number;
  rC: number;
  /** Σ received − Σ adjusted dispatch over the routes with a variance (n/a routes left out). */
  diff: number;
  /** diff / Σ adjusted × 100; null when nothing (≤ 0.5 t) was dispatched on those routes. */
  variance: number | null;
  latestDate: Date | null;
}

export const performanceTotals = (rows: RoutePerformanceRow[]): RoutePerformanceTotals | null => {
  if (!rows.length) return null;
  let dW = 0;
  let rW = 0;
  let dC = 0;
  let rC = 0;
  let adj = 0;
  let recv = 0;
  let latestDate: Date | null = null;
  for (const r of rows) {
    dW += r.dispatchWeight || 0;
    rW += r.receiptWeight || 0;
    dC += r.dispatchCount || 0;
    rC += r.receiptCount || 0;
    if (rowVariance(r) !== null) {
      adj += rowAdjustedDispatch(r);
      recv += r.receiptWeight || 0;
    }
    if (r.lastTicketDate) {
      const d = new Date(r.lastTicketDate);
      if (!latestDate || d > latestDate) latestDate = d;
    }
  }
  const diff = recv - adj;
  return {
    dW,
    rW,
    dC,
    rC,
    diff,
    variance: hasVarianceBase(adj) ? (diff / adj) * 100 : null,
    latestDate,
  };
};
